/* eslint-disable import/no-unused-modules */
import { useEffect, useRef, useState } from 'react';

import type { AnimationVariant, LottieAnimationHook } from './types';
import type { LottieRefCurrentProps } from '@/ui/Lottie';

const VARIANT_TO_ANIMATION_IMPORT = {
  confetti: async () => (await import('./lottie-animations/confetti.json')).default,
  'confetti-page': async () => (await import('./lottie-animations/confetti-page.json')).default,
} satisfies Record<AnimationVariant, () => Promise<unknown>>;

type AnimationData = Awaited<ReturnType<(typeof VARIANT_TO_ANIMATION_IMPORT)[AnimationVariant]>>;

const VARIANT_TO_ANIMATION_INTERVAL: Record<AnimationVariant, number> = {
  confetti: 100,
  'confetti-page': 500,
};

export const useLottieAnimation: LottieAnimationHook = ({ variant, loop = false, autoplay = false }) => {
  const [animationData, setAnimationData] = useState<AnimationData | null>(null);
  const [animationReady, setAnimationReady] = useState(false);
  const [isPlaying, setPlaying] = useState(autoplay);

  useEffect(() => {
    void VARIANT_TO_ANIMATION_IMPORT[variant]().then((animationData) => {
      setAnimationData(animationData);
    });
  }, [variant]);

  const ref = useRef<LottieRefCurrentProps | null>(null);
  const lottie = ref.current;

  const play = () => {
    setPlaying(true);
    lottie?.play();
  };
  const stop = () => {
    setPlaying(false);
    lottie?.stop();
  };
  const pause = () => {
    setPlaying(false);
    lottie?.pause();
  };

  const handlePlayingUpdate = (playing: boolean) => {
    setPlaying(playing);
  };

  useEffect(() => {
    if (animationReady && isPlaying) {
      lottie?.play();
    }
  }, [animationReady, isPlaying, lottie]);

  useEffect(() => {
    const animationInterval = VARIANT_TO_ANIMATION_INTERVAL[variant];
    if (!animationReady) {
      const interval = setInterval(() => {
        if (ref.current) {
          setAnimationReady(true);
        }
      }, animationInterval);
      return () => clearInterval(interval);
    }
  }, [animationReady]);

  const animationProps = {
    animationData,
    loop,
    autoplay,
    lottieRef: ref,
    isPlaying,
    onPlayingUpdate: handlePlayingUpdate,
    animationReady,
  };

  return {
    animationReady,
    ref,
    lottie,
    play,
    stop,
    pause,
    animationProps,
  };
};
